import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useCart } from "../context/CartContext";
import SearchBar from "../components/SearchBar";
import { FaCar, FaUsers } from "react-icons/fa";
import TravelerDetailsModal from "../components/TravelerDetailsModal";
import "../styles/ModernPages.css";
import usePageMeta from "../hooks/usePageMeta";
import { pageTitle } from "../lib/seo";

function CabsNew() {
  usePageMeta({
    title: pageTitle("Cab & Taxi Booking"),
    description: "Book reliable cabs and taxis for airport transfers, outstation trips and local sightseeing. Choose your car and seating capacity.",
  });
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [cabs, setCabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [countryFilter, setCountryFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [selectedCab, setSelectedCab] = useState(null);

  const [countries, setCountries] = useState([]);

  useEffect(() => {
    fetchCabs();
  }, []);

  async function fetchCabs() {
    try {
      const { data } = await supabase
        .from("cabs")
        .select("*")
        .order("country", { ascending: true });
      
      setCabs(data || []);
      
      // Extract unique countries
      const uniqueCountries = [...new Set(data?.map(c => c.country).filter(Boolean))];
      setCountries(uniqueCountries);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  }

  const filteredCabs = useMemo(() => {
    let result = [...cabs];

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (c) =>
          c.city?.toLowerCase().includes(query) ||
          c.country?.toLowerCase().includes(query)
      );
    }

    if (countryFilter !== "all") {
      result = result.filter((c) => c.country === countryFilter);
    }

    if (typeFilter !== "all") {
      result = result.filter((c) => c.type === typeFilter);
    }

    return result;
  }, [cabs, searchQuery, countryFilter, typeFilter]);

  function handleBook(cab) {
    setSelectedCab(cab);
    setShowModal(true);
  }

  function handleModalSubmit(travelerDetails) {
    if (!selectedCab) return;
    
    const cartItem = {
      id: `cab-${selectedCab.id}`,
      name: `${selectedCab.type} Cab - ${selectedCab.city}`,
      type: 'cab',
      price: `₹${selectedCab.base_fare || 0} base + ₹${selectedCab.price_per_km || 0}/km`,
      image: selectedCab.image,
      destination: `${selectedCab.city}, ${selectedCab.country}`,
      city: selectedCab.city,
      country: selectedCab.country,
    };
    
    addToCart(cartItem, travelerDetails);
    setShowModal(false);
    setSelectedCab(null);
    navigate("/cart");
  }

  return (
    <div className="cabs-new-page">
      <div className="page-hero">
        <h1>Premium Cab Services</h1>
        <p>Book reliable, comfortable rides across cities with professional drivers</p>
      </div>

      <div className="search-section">
        <div className="search-container">
          <SearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search by city or country..."
          />
        </div>
      </div>

      <div className="content-container">
        <div className="filters-bar">
          <select
            className="filter-select"
            value={countryFilter}
            onChange={(e) => setCountryFilter(e.target.value)}
          >
            <option value="all">All Countries</option>
            {countries.map((country) => (
              <option key={country} value={country}>
                {country}
              </option>
            ))}
          </select>

          <select
            className="filter-select"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="all">All Vehicle Types</option>
            <option value="Sedan">Sedan</option>
            <option value="SUV">SUV</option>
            <option value="Luxury">Luxury</option>
            <option value="Economy">Economy</option>
          </select>
        </div>

        {loading && (
          <div className="loading-container">
            <div className="loading-spinner"></div>
          </div>
        )}

        {!loading && filteredCabs.length === 0 && (
          <div className="empty-state">
            <div className="empty-state-icon">🚕</div>
            <h3>No cabs found</h3>
            <p>Try adjusting your filters</p>
          </div>
        )}

        {!loading && filteredCabs.length > 0 && (
          <>
            <div className="cards-grid">
              {filteredCabs.map((cab) => (
                <div key={cab.id} className="modern-card">
                  <div className="card-image">
                    <img
                      src={cab.image || "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=600"}
                      alt={cab.type}
                      loading="lazy"
                    />
                    <div className="card-badge">{cab.type}</div>
                  </div>

                  <div className="card-content">
                    <div className="card-location">{cab.city}, {cab.country}</div>
                    <h3 className="card-title">{cab.name || `${cab.type} Cab`}</h3>

                    <div className="card-meta">
                      <div className="meta-item">
                        <FaCar />
                        <span>{cab.type}</span>
                      </div>
                      <div className="meta-item">
                        <FaUsers />
                        <span>{cab.capacity || 4} seats</span>
                      </div>
                    </div>

                    <div className="card-footer">
                      <div className="card-price">
                        <div className="price-amount">₹{cab.base_fare || 0} + ₹{cab.price_per_km || 0}/km</div>
                        <div className="price-label">base + per km</div>
                      </div>
                      <button className="card-cta" onClick={() => handleBook(cab)}>
                        Book Now
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ textAlign: "center", marginTop: 40, color: "#64748b" }}>
              Showing {filteredCabs.length} cab{filteredCabs.length !== 1 ? "s" : ""}
            </div>
          </>
        )}
      </div>

      {/* Traveler Details Modal */}
      <TravelerDetailsModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setSelectedCab(null);
        }}
        onSubmit={handleModalSubmit}
        itemName={selectedCab ? `${selectedCab.type} Cab - ${selectedCab.city}` : ""}
      />
    </div>
  );
}

export default CabsNew;